import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Phone, Home, GraduationCap, Car, 
  Lock, Edit2, LogOut, ChevronRight, Info, Armchair
} from 'lucide-react';
import { ApiService } from '../services/apiEndpoints';
import { useAuth } from '../context/AuthContext';
import './Settings.css';

// Using GraduationCap for 'School' and 'Chair' for SeatRequest (we'll use Edit for seat)
const Settings = () => {
  const navigate = useNavigate();
  const { studentId, student, setStudent, logout } = useAuth();
  
  // File upload ref
  const fileInputRef = useRef(null);

  // General State
  const [updating, setUpdating] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarError, setAvatarError] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(0);

  // Generic Edit Dialog State
  const [editConfig, setEditConfig] = useState({ isOpen: false, field: null, title: "", value: "" });
  
  // Password Dialog State
  const [pwdConfig, setPwdConfig] = useState({ isOpen: false, oldPass: "", newPass: "", confirmPass: "" });
  const [passUpdating, setPassUpdating] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!studentId) {
      setAvatarUrl(null);
      setAvatarError(false);
      return undefined;
    }

    setAvatarUrl(null);
    setAvatarError(false);

    const loadStudentPhoto = async () => {
      try {
        const response = await ApiService.getStudentPhoto(studentId);
        const telegramUrl = response.data.url;

        if (isMounted) setAvatarUrl(telegramUrl);
      } catch (error) {
        if (isMounted) setAvatarError(true);
      }
    };

    loadStudentPhoto();

    return () => {
      isMounted = false;
    };
  }, [studentId, avatarVersion]);

  // --- HELPERS ---
  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3000);
  };

  const openEditor = (fieldKey, currentVal, title) => {
    setEditConfig({
      isOpen: true,
      field: fieldKey,
      title: title,
      value: currentVal || ""
    });
  };

  const closeEditor = () => setEditConfig({ isOpen: false, field: null, title: "", value: "" });

  // --- ACTIONS ---
  
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !studentId) return;

    setUpdating(true);
    try {
      const formData = new FormData();
      formData.append("studentId", studentId);
      formData.append("data", JSON.stringify({ photoUpdated: true }));
      formData.append("photo", file);

      const res = await ApiService.updateProfile(formData);
      if (res.status === 200) {
        // Show the selected photo immediately; the subsequent read replaces
        // this temporary browser URL with the server-hosted photo URL.
        const previewUrl = URL.createObjectURL(file);
        setStudent((current) => ({
          ...current,
          photoUrl: previewUrl,
        }));
        setAvatarUrl(previewUrl);
        setAvatarError(false);

        // Refresh student data to get new photo URL
        const studentRes = await ApiService.getStudent(studentId);
        if (studentRes.data) {
          setStudent({
            ...studentRes.data,
          });
        }
        setAvatarVersion((version) => version + 1);
        showToast("Photo updated successfully");
      }
    } catch (error) {
      showToast("Photo update failed");
    } finally {
      setUpdating(false);
      // Reset input so the same file can be picked again if needed
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleGenericUpdate = async () => {
    const { field, value } = editConfig;
    if (!field || !studentId) return;

    let cleanValue = value.trim();

    // Indian Vehicle Number Validation
    if (field === 'vehicleNumber') {
      cleanValue = cleanValue.replace(/[-\s]/g, '').toUpperCase();
      const vehicleRegex = /^[A-Z]{2}[0-9]{2}[A-Z]{1,2}[0-9]{4}$/;
      if (cleanValue && !vehicleRegex.test(cleanValue)) {
        showToast("Invalid Indian Vehicle Number");
        return;
      }
    }

    const payloadValue = field === 'desiredSeat' ? "change it" : cleanValue;

    setUpdating(true);
    try {
      const formData = new FormData();
      formData.append("studentId", studentId);
      formData.append("data", JSON.stringify({ [field]: payloadValue }));

      const res = await ApiService.updateProfile(formData);
      if (res.status === 200) {
        // Update Context first so every consumer re-renders immediately,
        // rather than waiting for a manual page refresh.
        if (field !== 'desiredSeat') {
          setStudent((current) => ({ ...current, [field]: cleanValue }));
        }

        // Refresh local student data
        const studentRes = await ApiService.getStudent(studentId);
        if (studentRes.data) {
          setStudent({
            ...studentRes.data,
          });
        }
        
        showToast(field === 'desiredSeat' ? "Seat request sent" : "Updated successfully");
        closeEditor();
      }
    } catch (error) {
      showToast("Update failed");
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordChange = async () => {
    const { oldPass, newPass, confirmPass } = pwdConfig;
    
    if (newPass !== confirmPass) {
      showToast("Passwords do not match");
      return;
    }

    setPassUpdating(true);
    try {
      await ApiService.changePassword({
        studentId,
        oldPassword: oldPass,
        newPassword: newPass
      });
      
      // Logout on success
      try { await ApiService.logout({ studentId }); } catch (e) {}
      logout();
      navigate('/app/signin');
      
    } catch (error) {
      const msg = error.response?.data?.includes("wrong_old_password") 
        ? "Old password incorrect" 
        : "Change failed. Try again.";
      showToast(msg);
    } finally {
      setPassUpdating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await ApiService.logout({ studentId });
    } catch (e) {}
    logout();
    navigate('/app/signin');
  };

  return (
    <div className="settings-screen">
      
      {/* Toast Notification */}
      {toastMsg && <div className="settings-toast">{toastMsg}</div>}

      {/* Top Bar */}
      <div className="settings-top-bar">
        <button onClick={() => navigate(-1)} className="icon-button">
          <ArrowLeft size={24} color="#1e3a8a" />
        </button>
        <h2>Settings</h2>
      </div>

      <div className="settings-content">
        
        {/* Profile Header */}
        <div className="profile-header">
          <div 
            className="profile-avatar-container" 
            onClick={() => !updating && fileInputRef.current?.click()}
          >
            {avatarError || !avatarUrl ? (
              <img
                src="/assets/logo.png"
                alt="Profile"
                className="profile-avatar"
                style={{ backgroundColor: 'white' }}
              />
            ) : (
              <img
                src={avatarUrl}
                alt="Profile"
                className="profile-avatar"
                style={{ backgroundColor: 'var(--card-bg)' }}
                onError={() => setAvatarError(true)}
              />
            )}
            <div className="edit-badge">
              {updating && !editConfig.isOpen ? (
                <div className="spinner tiny"></div>
              ) : (
                <Edit2 size={14} color="white" />
              )}
            </div>
            <input 
              type="file" 
              accept="image/*" 
              ref={fileInputRef} 
              onChange={handlePhotoChange} 
              hidden 
            />
          </div>
          <h2 className="profile-name">{student?.fullName || "Student"}</h2>
          <p className="profile-email">{student?.email || ""}</p>
        </div>

        {/* Group 1: Personal Info */}
        <div className="settings-group">
          <h3 className="group-title">PERSONAL INFORMATION</h3>
          <div className="group-card">
            <SettingsRow 
              icon={Phone} title="Phone Number" value={student?.phone}
              onClick={() => openEditor('phone', student?.phone, 'Update Phone Number')} 
            />
            <hr />
            <SettingsRow 
              icon={Home} title="Address" value={student?.address}
              onClick={() => openEditor('address', student?.address, 'Update Home Address')} 
            />
            <hr />
            <SettingsRow 
              icon={GraduationCap} title="Exam Preparation" value={student?.exam}
              onClick={() => openEditor('exam', student?.exam, 'Update Exam Target')} 
            />
            <hr />
            <SettingsRow 
              icon={Car} title="Vehicle Number" value={student?.vehicleNumber}
              onClick={() => openEditor('vehicleNumber', student?.vehicleNumber, 'Update Vehicle Number')} 
            />
            <hr />
            <SettingsRow 
              icon={Lock} title="Change Password" 
              onClick={() => setPwdConfig({ ...pwdConfig, isOpen: true })} 
            />
          </div>
        </div>

        {/* Group 2: Preferences */}
        <div className="settings-group">
          <h3 className="group-title">PREFERENCES & REQUESTS</h3>
          <div className="group-card">
            <SettingsRow 
              icon={Armchair} title="Request Seat Change" value="Tap to request"
              onClick={() => openEditor('desiredSeat', '', 'Request Seat Change')} 
            />
            <hr />
            <SettingsRow
              icon={Info} title="About Us" value="Library information"
              onClick={() => navigate('/app/about')}
            />
            <hr />
            <SettingsRow 
              icon={Info} title="App Version" value="v1.0.0" isClickable={false} 
            />
          </div>
        </div>

        {/* Logout Button */}
        <button className="settings-logout-btn" onClick={handleLogout}>
          <LogOut size={18} /> Logout
        </button>

      </div>

      {/* --- MODALS --- */}
      
      {/* Generic Edit Modal */}
      {editConfig.isOpen && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3>{editConfig.title}</h3>
            
            {editConfig.field === 'desiredSeat' ? (
              <p className="modal-desc">Are you sure you want to request a seat change?</p>
            ) : (
              <input 
                type="text" 
                className="modal-input" 
                placeholder="Enter new value"
                value={editConfig.value}
                onChange={(e) => setEditConfig({...editConfig, value: e.target.value})}
              />
            )}
            
            <div className="modal-actions">
              <button className="btn-cancel" onClick={closeEditor} disabled={updating}>Cancel</button>
              <button className="btn-confirm" onClick={handleGenericUpdate} disabled={updating}>
                {updating ? <div className="spinner small"></div> : (editConfig.field === 'desiredSeat' ? "Confirm" : "Save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {pwdConfig.isOpen && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3>Change Password</h3>
            <div className="pwd-inputs">
              <input 
                type="password" placeholder="Old Password" 
                className="modal-input"
                value={pwdConfig.oldPass} 
                onChange={(e) => setPwdConfig({...pwdConfig, oldPass: e.target.value})}
              />
              <input 
                type="password" placeholder="New Password" 
                className="modal-input"
                value={pwdConfig.newPass} 
                onChange={(e) => setPwdConfig({...pwdConfig, newPass: e.target.value})}
              />
              <input 
                type="password" placeholder="Confirm Password" 
                className="modal-input"
                value={pwdConfig.confirmPass} 
                onChange={(e) => setPwdConfig({...pwdConfig, confirmPass: e.target.value})}
              />
              {pwdConfig.confirmPass && pwdConfig.newPass !== pwdConfig.confirmPass && (
                <span className="error-text">Passwords do not match</span>
              )}
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={() => setPwdConfig({...pwdConfig, isOpen: false})} 
                disabled={passUpdating}
              >Cancel</button>
              <button 
                className="btn-confirm" 
                onClick={handlePasswordChange} 
                disabled={passUpdating || (pwdConfig.confirmPass && pwdConfig.newPass !== pwdConfig.confirmPass)}
              >
                {passUpdating ? <div className="spinner small"></div> : "Change"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Reusable Row Component
const SettingsRow = ({ icon: Icon, title, value, isClickable = true, onClick }) => (
  <div className={`settings-row ${isClickable ? 'clickable' : ''}`} onClick={isClickable ? onClick : undefined}>
    <div className="settings-row-icon">
      <Icon size={20} color="#1d4ed8" strokeWidth={2.5} />
    </div>
    <div className="settings-row-content">
      <span className="settings-row-title">{title}</span>
    </div>
    {value && (
      <span className="settings-row-value">
        {value.length > 20 ? value.substring(0, 18) + '...' : value}
      </span>
    )}
    {isClickable && <ChevronRight size={18} color="var(--text-light)" style={{ marginLeft: '8px' }} />}
  </div>
);

export default Settings;
